/**
 * The EVM settlement networks this build knows, in one place.
 *
 * Base (mainnet and Sepolia) stays the historical `REACT_APP_*` deployment:
 * its record is derived from that configuration and its behaviour is byte
 * identical. DogeOS Chikyu is a second, fully prefixed record
 * (`REACT_APP_DOGEOS_CHIKYU_*`) mounted at `/dogeos`, served only when its
 * pool is configured and fail-closed on any malformed value, exactly like the
 * Solana devnet record in `./solana`. DOGEOS_MYDOGE_JOINSPLIT_PLAN.md,
 * sections 5.3 and 6.3.
 */

export type EvmNetworkKey = "base" | "base-sepolia" | "dogeos-chikyu";

/** What `wallet_addEthereumChain` needs to add the chain to a wallet. */
export interface EvmWalletChainParams {
  chainId: string;
  chainName: string;
  nativeCurrency: { name: string; symbol: string; decimals: number };
  rpcUrls: string[];
  blockExplorerUrls: string[];
}

/**
 * The EIP-712 preimage the browser signs to derive its private wallet keys.
 * Every field is part of the key: two networks never share a descriptor.
 */
export interface EvmDerivationDescriptor {
  /** `kind` in the server challenge and the client transcript version. */
  version: string;
  domainName: string;
  primaryType: string;
  keyVersion: string;
  /** The sentence wallets show first (the `action` field). */
  statement?: string;
  /** Legacy layout only: the `purpose` field, after the location fields. */
  purpose?: string;
}

export interface EvmNetworkDefinition {
  key: EvmNetworkKey;
  chainId: number;
  label: string;
  route: string;
  /** Real value at stake: development artifacts are never acceptable. */
  production: boolean;
  nativeCurrency: { name: string; symbol: string; decimals: number };
  explorerUrl: string;
  /** Public JSON-RPC endpoints handed to wallets (never used by the app). */
  publicRpcUrls: string[];
  /** Proxy service keys (`/api/proxy/<service>`). */
  engineService: string;
  rpcService: string;
  derivation: EvmDerivationDescriptor;
  /** The descriptor signed before the current one. Wallets created with it
   * sign it once per device to keep their keys (walletCarryOver.ts). */
  legacyDerivation: EvmDerivationDescriptor;
}

const DERIVATION_STATEMENT =
  "Sign here to create your StealthOS private wallet. Signing is free and does not send a transaction.";

const BASE_DERIVATION: EvmDerivationDescriptor = Object.freeze({
  version: "base-eip712-v3",
  domainName: "StealthOS Private Wallet",
  primaryType: "StealthOSBasePrivateWallet",
  keyVersion: "3",
  statement: DERIVATION_STATEMENT,
});

// The historical values, byte for byte.
const LEGACY_BASE_DERIVATION: EvmDerivationDescriptor = Object.freeze({
  version: "base-eip712-v2",
  domainName: "StealthOS Base Privacy Keys",
  primaryType: "StealthOSBasePrivacyKey",
  keyVersion: "2",
  purpose: "derive StealthOS Base private wallet keys",
});

const DOGEOS_DERIVATION: EvmDerivationDescriptor = Object.freeze({
  version: "dogeos-eip712-v2",
  domainName: "StealthOS Private Wallet",
  primaryType: "StealthOSDogeOSPrivateWallet",
  keyVersion: "2",
  statement: DERIVATION_STATEMENT,
});

const LEGACY_DOGEOS_DERIVATION: EvmDerivationDescriptor = Object.freeze({
  version: "dogeos-eip712-v1",
  domainName: "StealthOS DogeOS Privacy Keys",
  primaryType: "StealthOSDogeOSPrivacyKey",
  keyVersion: "1",
  purpose: "derive StealthOS DogeOS private wallet keys",
});

const ETHER = Object.freeze({ name: "Ether", symbol: "ETH", decimals: 18 });
const DOGE = Object.freeze({ name: "Dogecoin", symbol: "DOGE", decimals: 18 });

export const EVM_NETWORK_DEFINITIONS: Readonly<
  Record<number, EvmNetworkDefinition>
> = Object.freeze({
  8453: Object.freeze({
    key: "base" as const,
    chainId: 8453,
    label: "Base",
    route: "/base",
    production: true,
    nativeCurrency: ETHER,
    explorerUrl: "https://basescan.org",
    publicRpcUrls: ["https://mainnet.base.org"],
    engineService: "base-engine",
    rpcService: "evm-rpc",
    derivation: BASE_DERIVATION,
    legacyDerivation: LEGACY_BASE_DERIVATION,
  }),
  84532: Object.freeze({
    key: "base-sepolia" as const,
    chainId: 84532,
    label: "Base Sepolia",
    route: "/base",
    production: false,
    nativeCurrency: ETHER,
    explorerUrl: "https://sepolia.basescan.org",
    publicRpcUrls: ["https://sepolia.base.org"],
    engineService: "base-engine",
    rpcService: "evm-rpc",
    derivation: BASE_DERIVATION,
    legacyDerivation: LEGACY_BASE_DERIVATION,
  }),
  6281971: Object.freeze({
    key: "dogeos-chikyu" as const,
    chainId: 6281971,
    label: "DogeOS Chikyu",
    route: "/dogeos",
    production: false,
    nativeCurrency: DOGE,
    explorerUrl: "https://blockscout.testnet.dogeos.com",
    publicRpcUrls: ["https://rpc.testnet.dogeos.com"],
    engineService: "dogeos-engine",
    rpcService: "dogeos-rpc",
    derivation: DOGEOS_DERIVATION,
    legacyDerivation: LEGACY_DOGEOS_DERIVATION,
  }),
});

export const DOGEOS_CHIKYU_CHAIN_ID = 6281971;

/** Chains that may run with development proving artifacts and verifiers. */
export const DEVELOPMENT_EVM_CHAIN_IDS: ReadonlySet<number> = new Set([
  84532,
  DOGEOS_CHIKYU_CHAIN_ID,
  31337,
  1337,
]);

export function evmNetworkDefinition(
  chainId: number,
): EvmNetworkDefinition | undefined {
  return EVM_NETWORK_DEFINITIONS[Number(chainId)];
}

export function evmNetworkDefinitionByKey(
  key: string,
): EvmNetworkDefinition | undefined {
  return Object.values(EVM_NETWORK_DEFINITIONS).find(
    (network) => network.key === key,
  );
}

/** The `wallet_addEthereumChain` parameters for a chain the app knows. */
export function evmWalletChainParams(
  chainId: number,
  overrides: { explorerUrl?: string; publicRpcUrls?: string[] } = {},
): EvmWalletChainParams {
  const definition = evmNetworkDefinition(chainId);
  const hex = `0x${Number(chainId).toString(16)}`;
  if (!definition) {
    // The historical fallback: an unknown chain id is treated as a Base test
    // deployment, as `useBaseWallet` always did.
    const sepolia = EVM_NETWORK_DEFINITIONS[84532];
    return {
      chainId: hex,
      chainName: sepolia.label,
      nativeCurrency: { ...sepolia.nativeCurrency },
      rpcUrls: [...sepolia.publicRpcUrls],
      blockExplorerUrls: [sepolia.explorerUrl],
    };
  }
  return {
    chainId: hex,
    chainName: definition.label,
    nativeCurrency: { ...definition.nativeCurrency },
    rpcUrls: [...(overrides.publicRpcUrls ?? definition.publicRpcUrls)],
    blockExplorerUrls: [overrides.explorerUrl ?? definition.explorerUrl],
  };
}

// ---------------------------------------------------------------------------
// DogeOS Chikyu: the prefixed, fail-closed record.
// ---------------------------------------------------------------------------

type Env = Record<string, string | undefined>;

export interface DogeOsChikyuConfig {
  key: "dogeos-chikyu";
  chainId: number;
  label: string;
  route: string;
  /** Served: the pool is configured and every variable parsed. */
  enabled: boolean;
  /** Set when a variable is malformed; the network is then disabled. */
  configurationError?: string;
  shieldedPoolJoinSplit: string;
  joinSplitRouter: string;
  /** Stable privacy-key domain; independent of replacement settlement pools. */
  derivationPool: string;
  derivationVerifier: string;
  deploymentBlock: number;
  finalityBlocks: number;
  logChunkSize: number;
  relayWithdrawals: boolean;
  relayFeeBps: number;
  /** Floor for a relayed fee, in wei of DOGE (the relayer prices gas). */
  relayMinFeeWei: bigint;
  /** Price relayed withdrawals with the relayer's live pre-proof quote
   * (POST /api/v1/relay/pool/quote) instead of the fixed floor. */
  relayFeeQuotes: boolean;
  /** Refresh the private balance incrementally, like Base Sepolia: skip when
   * the data service's verified sync head is unchanged, download only deltas,
   * and keep block-pinned local tree checkpoints (the Chikyu RPC serves
   * historical state and the canonical Multicall3). */
  incrementalSync: boolean;
  allowDevelopmentArtifacts: boolean;
  /** Largest circuit arity a settlement may use on this chain (D10). */
  maxArityId: number;
  explorerUrl: string;
  publicRpcUrls: string[];
  engineProxyUrl: string;
  rpcProxyUrl: string;
}

export const DOGEOS_CHIKYU_ENV_PREFIX = "REACT_APP_DOGEOS_CHIKYU_";
/** Chikyu's block gas limit is 10M: 8x5 fits, 13x5 and 32x5 do not (D10). */
export const DOGEOS_CHIKYU_DEFAULT_MAX_ARITY_ID = 2;

function text(env: Env, name: string): string {
  return String(env[name] ?? "").trim();
}

function integer(
  env: Env,
  name: string,
  fallback: number,
  errors: string[],
  { min, max = Number.MAX_SAFE_INTEGER }: { min: number; max?: number },
): number {
  const raw = text(env, name);
  if (!raw) return fallback;
  const parsed = Number(raw);
  if (!Number.isSafeInteger(parsed) || parsed < min || parsed > max) {
    errors.push(`${name} must be an integer between ${min} and ${max}`);
    return fallback;
  }
  return parsed;
}

function flag(
  env: Env,
  name: string,
  fallback: boolean,
  errors: string[],
): boolean {
  const raw = text(env, name).toLowerCase();
  if (!raw) return fallback;
  if (raw === "true") return true;
  if (raw === "false") return false;
  errors.push(`${name} must be true or false`);
  return fallback;
}

function address(
  env: Env,
  name: string,
  errors: string[],
  { required }: { required: boolean },
): string {
  const raw = text(env, name);
  if (!raw) {
    if (required) errors.push(`${name} is required`);
    return "";
  }
  if (!/^0x[0-9a-fA-F]{40}$/.test(raw) || /^0x0{40}$/.test(raw)) {
    errors.push(`${name} must be a non-zero EVM address`);
    return "";
  }
  return raw;
}

function httpsUrl(env: Env, name: string, fallback: string, errors: string[]) {
  const raw = text(env, name) || fallback;
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:") throw new Error("not https");
    return url.toString().replace(/\/+$/, "");
  } catch {
    errors.push(`${name} must be an https URL`);
    return fallback;
  }
}

/** Parse a decimal DOGE amount ("0.5") into wei (18 decimals). */
export function parseDogeAmount(value: string): bigint {
  const trimmed = String(value ?? "").trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) {
    throw new Error(`"${value}" is not a decimal amount`);
  }
  const [whole, fraction = ""] = trimmed.split(".");
  if (fraction.length > 18) {
    throw new Error(`"${value}" has more than 18 decimal places`);
  }
  return BigInt(whole + fraction.padEnd(18, "0"));
}

function dogeAmount(
  env: Env,
  name: string,
  fallback: string,
  errors: string[],
): bigint {
  const raw = text(env, name) || fallback;
  try {
    return parseDogeAmount(raw);
  } catch (error) {
    errors.push(
      `${name}: ${error instanceof Error ? error.message : String(error)}`,
    );
    return parseDogeAmount(fallback);
  }
}

/**
 * Reads the DogeOS Chikyu record. Never throws: a malformed variable
 * disables the network with a `configurationError`, an unset pool disables
 * it silently, and the Base app is never affected either way.
 */
export function readDogeOsChikyuConfig(env: Env): DogeOsChikyuConfig {
  const definition = EVM_NETWORK_DEFINITIONS[DOGEOS_CHIKYU_CHAIN_ID];
  const p = DOGEOS_CHIKYU_ENV_PREFIX;
  const errors: string[] = [];
  const pool = address(env, `${p}SHIELDED_POOL_JOINSPLIT`, errors, {
    required: false,
  });
  const configured = Boolean(text(env, `${p}SHIELDED_POOL_JOINSPLIT`));
  const router = address(env, `${p}JOINSPLIT_ROUTER`, errors, {
    required: configured,
  });
  const pinnedDerivation = Boolean(
    text(env, `${p}DERIVATION_POOL`) || text(env, `${p}DERIVATION_VERIFIER`),
  );
  const derivationPool = address(env, `${p}DERIVATION_POOL`, errors, {
    required: pinnedDerivation,
  });
  const derivationVerifier = address(env, `${p}DERIVATION_VERIFIER`, errors, {
    required: pinnedDerivation,
  });
  const deploymentBlock = integer(env, `${p}DEPLOYMENT_BLOCK`, 0, errors, {
    min: 0,
  });
  if (configured && deploymentBlock <= 0)
    errors.push(`${p}DEPLOYMENT_BLOCK must be the pool's deployed block`);
  const config: DogeOsChikyuConfig = {
    key: "dogeos-chikyu",
    chainId: definition.chainId,
    label: definition.label,
    route: definition.route,
    enabled: false,
    shieldedPoolJoinSplit: pool,
    joinSplitRouter: router,
    derivationPool: derivationPool || pool,
    derivationVerifier: derivationVerifier || router,
    deploymentBlock,
    // Measured on 2026-09-29 (docs/dogeos/chikyu-probe-2026-09-29.json):
    // 12 blocks and 10k-block log windows are what the RPC serves reliably.
    finalityBlocks: integer(env, `${p}FINALITY_BLOCKS`, 12, errors, {
      min: 1,
      max: 10_000,
    }),
    logChunkSize: integer(env, `${p}LOG_CHUNK_SIZE`, 10_000, errors, {
      min: 1,
      max: 10_000,
    }),
    relayWithdrawals: flag(env, `${p}RELAY_WITHDRAWALS`, true, errors),
    relayFeeBps: integer(env, `${p}RELAY_FEE_BPS`, 30, errors, {
      min: 0,
      max: 10_000,
    }),
    relayMinFeeWei: dogeAmount(env, `${p}RELAY_MIN_FEE_DOGE`, "0.01", errors),
    relayFeeQuotes: flag(env, `${p}RELAY_FEE_QUOTES`, true, errors),
    // Chikyu's hosted index can follow safe/depth-fallback history ahead of
    // finalized. Use the canonical finalized full sync until that feed carries
    // a finalized, hash-pinned cursor; an env flag must not bypass this boundary.
    incrementalSync: flag(env, `${p}INCREMENTAL_SYNC`, false, errors) && false,
    allowDevelopmentArtifacts: flag(
      env,
      `${p}ALLOW_DEVELOPMENT_ARTIFACTS`,
      false,
      errors,
    ),
    maxArityId: integer(
      env,
      `${p}MAX_ARITY_ID`,
      DOGEOS_CHIKYU_DEFAULT_MAX_ARITY_ID,
      errors,
      { min: 0, max: 4 },
    ),
    explorerUrl: httpsUrl(
      env,
      `${p}EXPLORER_URL`,
      definition.explorerUrl,
      errors,
    ),
    publicRpcUrls: [
      httpsUrl(env, `${p}PUBLIC_RPC_URL`, definition.publicRpcUrls[0], errors),
    ],
    engineProxyUrl: `/api/proxy/${definition.engineService}?path=`,
    rpcProxyUrl: `/api/proxy/${definition.rpcService}`,
  };
  if (errors.length > 0) {
    return { ...config, enabled: false, configurationError: errors.join("; ") };
  }
  return { ...config, enabled: configured };
}

/**
 * The EVM network a page path belongs to. `/dogeos` and `/dogeos/send` are
 * DogeOS Chikyu; every other EVM path is the Base deployment. Read once at
 * module load, like `PREVIOUS_POOL_MODE`, so every module-scope consumer of
 * `EVM_CONFIG` sees one network for the life of the page.
 */
export function evmNetworkKeyForPath(pathname: string): EvmNetworkKey {
  const normalized = String(pathname || "/").replace(/\/+$/, "") || "/";
  const dogeos = EVM_NETWORK_DEFINITIONS[DOGEOS_CHIKYU_CHAIN_ID].route;
  return normalized === dogeos || normalized === `${dogeos}/send`
    ? "dogeos-chikyu"
    : "base";
}
